import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { GestureEvent, ViewChangedPayload } from "./types";
import { GestureType } from "./types";

/**
 * Zoom configuration.
 */
interface ZoomConfig {
  /** Multiplier for mouse wheel delta */
  wheelZoomFactor: number;
  /** Smoothing coefficient for pinch zoom */
  pinchSmoothingFactor: number;
  /** Minimum absolute pixel change in pinch distance to trigger zoom; filters jitter during 2-finger pan */
  pinchDistanceThreshold: number;
}

/** Pause after the last wheel notch that ends a wheel zoom. */
const WHEEL_END_DELAY_MS = 200;

const DEFAULT_ZOOM_CONFIG: ZoomConfig = {
  wheelZoomFactor: 0.01,
  pinchSmoothingFactor: 1,
  pinchDistanceThreshold: 2,
};

/**
 * NavigationHandler - Handles pan and zoom navigation.
 *
 * Note: Module.App handles navigation internally via its own event handlers.
 * This class provides additional control when we need to:
 * - Perform programmatic navigation (zoom extents, zoom to point)
 * - Handle wheel zoom with custom behavior
 * - Coordinate navigation state with the interaction manager
 *
 * For most cases, Module.App's built-in pan/zoom is sufficient.
 */
export class NavigationHandler {
  private viewer: DRDrawingViewer;
  private config: ZoomConfig;

  // Navigation state
  private isPanning = false;
  private isZooming = false;
  private lastPinchCenter: { x: number; y: number } | null = null;

  // View change event handlers
  private viewChangeHandlers: Array<(payload: ViewChangedPayload) => void> = [];

  // Navigation active event handlers
  private navigationActiveHandlers: Array<(active: boolean) => void> = [];
  // Navigation in progress, by gesture (finger, mouse drag) or by wheel.
  private gestureNavigating = false;
  private wheelNavigating = false;
  private wheelEndTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(viewer: DRDrawingViewer, config: Partial<ZoomConfig> = {}) {
    this.viewer = viewer;
    this.config = { ...DEFAULT_ZOOM_CONFIG, ...config };

    // Attach wheel zoom by default
    this.attachWheelZoom();
  }

  // ============== View Change Events ==============

  /**
   * Subscribe to view change events.
   * Called when pan/zoom/resize occurs.
   * @returns Unsubscribe function
   */
  onViewChanged(handler: (payload: ViewChangedPayload) => void): () => void {
    this.viewChangeHandlers.push(handler);
    return () => {
      const index = this.viewChangeHandlers.indexOf(handler);
      if (index >= 0) {
        this.viewChangeHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Emit view changed event to all subscribers.
   */
  private emitViewChanged(changeType: ViewChangedPayload["changeType"]): void {
    const payload: ViewChangedPayload = { changeType };
    for (const handler of this.viewChangeHandlers) {
      handler(payload);
    }
  }

  // ============== Navigation Active Events ==============

  /**
   * Subscribe to navigation active state changes.
   * Fires true when a navigation starts (gesture of any pointer, or wheel
   * zoom), false when it ends: the rulers and the selection are not redrawn
   * while the view moves.
   * @returns Unsubscribe function
   */
  onNavigationActive(handler: (active: boolean) => void): () => void {
    this.navigationActiveHandlers.push(handler);
    return () => {
      const index = this.navigationActiveHandlers.indexOf(handler);
      if (index >= 0) {
        this.navigationActiveHandlers.splice(index, 1);
      }
    };
  }

  private emitNavigationActive(active: boolean): void {
    for (const handler of this.navigationActiveHandlers) {
      handler(active);
    }
  }

  /** Emits only when the overall state changes (gesture or wheel). */
  private setNavigationActive(
    source: "gesture" | "wheel",
    active: boolean,
  ): void {
    const wasActive = this.gestureNavigating || this.wheelNavigating;
    if (source === "gesture") this.gestureNavigating = active;
    else this.wheelNavigating = active;
    const isActive = this.gestureNavigating || this.wheelNavigating;
    if (isActive !== wasActive) this.emitNavigationActive(isActive);
  }

  // ============== Lifecycle ==============

  /**
   * Attach wheel event listener for custom zoom behavior.
   */
  attachWheelZoom(): void {
    this.viewer.canvas.addEventListener("wheel", this.handleWheel, {
      passive: false,
    });
  }

  /**
   * Detach wheel event listener.
   */
  detachWheelZoom(): void {
    this.viewer.canvas.removeEventListener("wheel", this.handleWheel);
  }

  /**
   * Dispose the navigation handler.
   */
  dispose(): void {
    this.detachWheelZoom();
    if (this.wheelEndTimer) clearTimeout(this.wheelEndTimer);
    this.wheelEndTimer = null;
    this.isPanning = false;
    this.isZooming = false;
  }

  // ============== Wheel Zoom ==============

  private handleWheel = (ev: WheelEvent): void => {
    ev.preventDefault();

    // Get mouse position relative to canvas
    const rect = this.viewer.canvas.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;

    // Module.App.Zoom expects: Zoom(delta, x, y)
    // Negative deltaY (scroll up) = zoom in (positive delta)
    const zoomDelta = -ev.deltaY * this.config.wheelZoomFactor;
    // A wheel has no start or end: the zoom lasts while the notches follow
    // each other.
    this.setNavigationActive("wheel", true);
    this.zoomAtPoint(x, y, zoomDelta);
    if (this.wheelEndTimer) clearTimeout(this.wheelEndTimer);
    this.wheelEndTimer = setTimeout(() => {
      this.wheelEndTimer = null;
      this.setNavigationActive("wheel", false);
    }, WHEEL_END_DELAY_MS);
  };

  // ============== Programmatic Navigation ==============

  /**
   * Zoom at a specific point using App.Zoom (for wheel zoom).
   * Positive delta = zoom in, negative = zoom out
   */
  zoomAtPoint(x: number, y: number, delta: number): void {
    const app = this.viewer.app;
    app.Zoom(delta, x, y);
    this.viewer.update();
    this.emitViewChanged("zoom");
  }

  /**
   * Zoom by a factor using OdGsView.zoom (for pinch zoom).
   * Factor > 1 = zoom in, factor < 1 = zoom out
   * If center is provided, zoom around that point instead of screen center.
   */
  zoomByFactor(factor: number, center?: { x: number; y: number }): void {
    // If center provided, compensate with pan to keep that point stationary
    if (center) {
      const screenCenterX = this.viewer.canvas.width / 2;
      const screenCenterY = this.viewer.canvas.height / 2;
      const offsetX = center.x - screenCenterX;
      const offsetY = center.y - screenCenterY;
      // After zoom, offset scales by factor, so pan by the difference
      const panX = offsetX * (1 - factor);
      const panY = offsetY * (1 - factor);
      this.pan(panX, panY);
    }

    const gsDevice = this.viewer.app.getDevice();
    const gsView = gsDevice.viewAt(0);
    gsView.zoom(factor);
    this.viewer.update();
    this.emitViewChanged("pinch");
  }

  /**
   * Zoom to fit all content.
   */
  zoomExtents(): void {
    this.viewer.zoomExtents();
    this.viewer.update();
    this.emitViewChanged("extents");
  }

  /**
   * Pan by a delta amount.
   */
  pan(deltaX: number, deltaY: number): void {
    const app = this.viewer.app;
    if (app && typeof (app as any).Dolly === "function") {
      (app as any).Dolly(deltaX, deltaY);
    }
    this.viewer.update();
    this.emitViewChanged("pan");
  }

  // ============== Gesture-Based Navigation ==============

  /**
   * Handle gesture start for navigation.
   */
  onNavigationStart(event: GestureEvent): void {
    if (event.type === GestureType.Drag) {
      this.isPanning = true;
    } else if (event.type === GestureType.Pinch) {
      this.isZooming = true;
      this.lastPinchCenter = event.pinchCenter ?? null;
    }
    this.setNavigationActive("gesture", true);
  }

  /**
   * Handle gesture move for navigation.
   */
  onNavigationMove(event: GestureEvent): void {
    if (this.isPanning && event.delta) {
      // Pan by the delta amount
      this.pan(event.delta.x, event.delta.y);
    }

    if (this.isZooming && event.pinchCenter) {
      // Two-finger pan: when both fingers move together
      if (this.lastPinchCenter) {
        const deltaX = event.pinchCenter.x - this.lastPinchCenter.x;
        const deltaY = event.pinchCenter.y - this.lastPinchCenter.y;
        if (Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5) {
          this.pan(deltaX, deltaY);
        }
      }
      this.lastPinchCenter = event.pinchCenter;

      // Two-finger zoom: when fingers spread or pinch
      if (event.pinchDistance && event.previousPinchDistance) {
        const scale = event.pinchDistance / event.previousPinchDistance;
        if (
          Math.abs(event.pinchDistance - event.previousPinchDistance) >
          this.config.pinchDistanceThreshold
        ) {
          const zoomFactor = 1 + (scale - 1) * this.config.pinchSmoothingFactor;
          this.zoomByFactor(zoomFactor, event.pinchCenter);
        }
      }
    }
  }

  /**
   * Handle gesture end for navigation.
   */
  onNavigationEnd(_event: GestureEvent): void {
    this.isPanning = false;
    this.isZooming = false;
    this.lastPinchCenter = null;
    this.setNavigationActive("gesture", false);
  }

  // ============== State ==============

  /**
   * Check if navigation is active.
   */
  get isActive(): boolean {
    return this.isPanning || this.isZooming;
  }

  /**
   * Cancel any active navigation.
   */
  cancel(): void {
    this.isPanning = false;
    this.isZooming = false;
    this.lastPinchCenter = null;
  }
}
